/**
 * BaseFilters - Componente base reutilizable para filtros de tabs admin
 * 
 * Proporciona estructura común para filtros con:
 * - Input de búsqueda con icono
 * - DateRangePresetPicker
 * - Botón para limpiar filtros
 * - Slot para filtros adicionales específicos
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DateRangePresetPicker from "@/components/v2/molecules/admin/shared/DateRangePresetPicker";
import { Search, X } from "lucide-react";
import { ReactNode } from "react";

export interface BaseFiltersProps {
    /** Término de búsqueda */
    searchTerm: string;
    /** Callback cuando cambia el término de búsqueda */
    onSearchChange: (value: string) => void;
    /** Placeholder para el input de búsqueda */
    searchPlaceholder?: string;
    /** Ancho del input de búsqueda */
    searchWidth?: string;
    /** Fecha desde */
    dateFrom?: Date;
    /** Fecha hasta */
    dateTo?: Date;
    /** Callback cuando cambia el rango de fechas */
    onDateRangeChange?: (from: Date | undefined, to: Date | undefined) => void;
    /** Si hay filtros activos */
    hasActiveFilters: boolean;
    /** Callback para limpiar todos los filtros */
    onClearFilters: () => void;
    /** Filtros adicionales específicos del tab (slot) */
    children?: ReactNode;
    /** Ocultar input de búsqueda */
    hideSearch?: boolean;
    /** Ocultar date picker */
    hideDatePicker?: boolean;
}

const BaseFilters = ({
    searchTerm,
    onSearchChange,
    searchPlaceholder = "Buscar...",
    searchWidth = "w-[240px]",
    dateFrom,
    dateTo,
    onDateRangeChange,
    hasActiveFilters,
    onClearFilters,
    children,
    hideSearch = false,
    hideDatePicker = false,
}: BaseFiltersProps) => {
    const handleDateRangeChange = (from: Date | undefined, to: Date | undefined) => {
        onDateRangeChange?.(from, to);
    };

    return (
        <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
                {/* Búsqueda */}
                {!hideSearch && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className={`pl-10 ${searchWidth} rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0`}
                        />
                    </div>
                )}

                {/* Filtros adicionales específicos del tab */}
                {children}
            </div>

            <div className="flex items-center gap-2">
                {/* Date Range Picker */}
                {!hideDatePicker && onDateRangeChange && (
                    <DateRangePresetPicker
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateRangeChange={handleDateRangeChange}
                    />
                )}

                {/* Botón limpiar filtros */}
                {hasActiveFilters && (
                    <Button
                        variant="ghost"
                        onClick={onClearFilters}
                        size="icon"
                        className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default BaseFilters;
